import React from 'react';
import { BRAND_LOGO_DARK_SRC, BRAND_LOGO_LIGHT_SRC, COMPANY_NAME, PRODUCT_NAME } from '../brand';
import { openStorageSettings } from '../privacy/storageConsent';
import './LegalPage.css';

export type LegalTopic = 'terms' | 'privacy' | 'cookies' | 'accessibility';

const UPDATED_AT = 'August 31, 2026';
const TERMS_UPDATED_AT = 'November 3, 2026';
const CONTACT_EMAIL = 'support@vectormethods.com';

interface LegalPageProps {
  topic: LegalTopic;
}

const legalNav: Array<{ topic: LegalTopic; label: string; href: string }> = [
  { topic: 'terms', label: 'Terms', href: '/terms' },
  { topic: 'privacy', label: 'Privacy', href: '/privacy' },
  { topic: 'cookies', label: 'Cookies', href: '/cookies' },
  { topic: 'accessibility', label: 'Accessibility', href: '/accessibility' }
];

const LegalPage: React.FC<LegalPageProps> = ({ topic }) => {
  const title = getTitle(topic);

  return (
    <div className="legal-page">
      <header className="legal-header">
        <a className="legal-brand" href="/" aria-label="Return to sign in">
          <img
            className="legal-brand-logo legal-brand-logo-light"
            src={BRAND_LOGO_LIGHT_SRC}
            alt=""
            aria-hidden="true"
          />
          <img
            className="legal-brand-logo legal-brand-logo-dark"
            src={BRAND_LOGO_DARK_SRC}
            alt=""
            aria-hidden="true"
          />
          <span className="legal-brand-product">{PRODUCT_NAME}</span>
        </a>
        <nav className="legal-nav" aria-label="Legal pages">
          {legalNav.map((item) => (
            <a
              key={item.topic}
              href={item.href}
              className={item.topic === topic ? 'active' : undefined}
              aria-current={item.topic === topic ? 'page' : undefined}
            >
              {item.label}
            </a>
          ))}
        </nav>
      </header>

      <main className="legal-main">
        <article className="legal-document">
          <div className="legal-kicker">VectorMethods legal</div>
          <h1>{title}</h1>
          <p className="legal-updated">
            Last updated: {topic === 'terms' ? TERMS_UPDATED_AT : topic === 'privacy' ? 'September 5, 2026' : UPDATED_AT}
          </p>
          {renderTopic(topic)}
        </article>
      </main>
    </div>
  );
};

function getTitle(topic: LegalTopic) {
  switch (topic) {
    case 'terms':
      return 'Terms of Service';
    case 'privacy':
      return 'Privacy Policy';
    case 'cookies':
      return 'Cookie and Storage Notice';
    case 'accessibility':
      return 'Accessibility Statement';
    default:
      return 'Legal';
  }
}

function renderTopic(topic: LegalTopic) {
  switch (topic) {
    case 'terms':
      return <TermsContent />;
    case 'privacy':
      return <PrivacyContent />;
    case 'cookies':
      return <CookiesContent />;
    case 'accessibility':
      return <AccessibilityContent />;
    default:
      return null;
  }
}

const TermsContent: React.FC = () => (
  <>
    <section>
      <h2>Agreement</h2>
      <p>
        These Terms govern access to {PRODUCT_NAME}, operated by {COMPANY_NAME}.
        By creating an account, signing in, or using the service, you agree to these Terms
        and to the Privacy Policy.
      </p>
      <p>
        The service is intended for business and professional use. You must be at least the
        age of majority where you live, or have authority from your organization, to use it.
      </p>
    </section>

    <section>
      <h2>Accounts</h2>
      <p>
        You are responsible for accurate account information, keeping credentials secure,
        and all activity under your account. Notify us promptly if you believe an account,
        API key, webhook, or integration has been compromised.
      </p>
    </section>

    <section>
      <h2>Customer Content</h2>
      <p>
        “Customer Content” includes videos, images, audio recordings, text, documents,
        prompts, metadata, and other materials you submit or make available to us, including
        through uploads, URLs, APIs, connectors, integrations, or persons acting on your behalf.
      </p>
      <p>
        You keep ownership of your Customer Content. You grant {COMPANY_NAME} a limited right to host, process,
        transmit, display, and analyze that content only to provide, secure, support, and
        improve the service.
      </p>
      <p>
        Each time you submit Customer Content, you expressly represent and warrant that,
        for every copyright-protected element, you own the necessary rights or have obtained
        valid authorization from the copyright owner or an authorized licensor covering
        your submission and all processing permitted under these Terms. This includes any
        embedded music, recordings, images, footage, and other third-party material. Public
        availability or possession of a copy does not, by itself, establish authorization.
      </p>
      <p>
        You must obtain and maintain all required permissions, licenses, notices, consents,
        and lawful bases, including those relating to privacy, personal information, likenesses,
        voices, confidentiality, and other third-party rights. You must provide reasonable
        evidence of your authorization upon request.
      </p>
      <p>
        As between you and {COMPANY_NAME}, you are solely responsible and liable for your
        Customer Content and the lawfulness of its submission and use. Our receipt, storage,
        processing, or review of content does not establish that you have the required rights.
      </p>
    </section>

    <section>
      <h2>Acceptable Use</h2>
      <p>
        Do not use the service to break the law, reverse engineer the service, bypass security controls, overload systems,
        scrape data, or attempt unauthorized access to accounts,
        infrastructure, models, data, or third-party services.
      </p>
      <p>
        You must not submit, store, transmit, request processing of, or use Customer Content
        where its possession, collection, submission, processing, or use violates applicable
        law or third-party rights.
      </p>
      <p>
        Prohibited material includes infringing copies; child sexual abuse or exploitation
        material; unlawful non-consensual intimate imagery, including synthetic or manipulated
        imagery; unlawfully obtained private recordings or personal information; malware;
        and material used to facilitate unlawful exploitation, trafficking, threats,
        terrorist recruitment, or incitement to violence.
      </p>
      <p>
        These restrictions apply regardless of format, whether content is real or synthetic,
        or whether it is publicly accessible or privately stored. You must not evade content
        safeguards or resubmit content restricted for violating these Terms.
      </p>
    </section>

    <section>
      <h2>Content Inspection and AI Screening</h2>
      <p>
        You authorize {COMPANY_NAME} and service providers acting on our behalf to inspect
        and analyze Customer Content and associated metadata at submission and while stored,
        using automated systems, artificial intelligence, content fingerprinting, hash matching,
        and authorized human review, to identify suspected infringement, prohibited material,
        abuse, security threats, and violations of these Terms.
      </p>
      <p>
        Your existing content license includes the limited rights necessary to perform
        these activities and the enforcement, preservation, and legal-compliance activities
        described below. These activities remain subject to applicable law, our Privacy
        Policy, and any controlling written agreement.
      </p>
      <p>
        Screening may produce errors or fail to identify violations. A screening result
        is not a legal determination, and acceptance or continued availability of content
        is not approval or confirmation of authorization. Except where required by law,
        we undertake no obligation to inspect every submission or detect every violation.
      </p>
    </section>

    <section>
      <h2>Outputs and Availability</h2>
      <p>
        Search results, metadata, summaries, labels, transcripts, AI-generated outputs, and
        other automated results may be incomplete, delayed, or inaccurate. You must review
        and validate outputs before relying on them for business, legal, safety, compliance,
        or other consequential decisions.
      </p>
      <p>
        The service may change, pause, or become unavailable from time to time for maintenance,
        security, usage limits, provider interruptions, or other operational reasons.
      </p>
    </section>

    <section>
      <h2>Billing</h2>
      <p>
        Paid features, credits, usage, renewals, taxes, and refunds are handled through the
        billing terms shown in the product or by the applicable payment provider. Fees are
        non-refundable except where required by law or expressly stated in writing.
      </p>
    </section>

    <section>
      <h2>Disclaimers</h2>
      <p>
        To the fullest extent permitted by law, the service is provided as is and as available,
        without warranties of merchantability, fitness for a particular purpose, non-infringement,
        uninterrupted operation, accuracy, or error-free performance.
      </p>
    </section>

    <section>
      <h2>Liability</h2>
      <p>
        To the fullest extent permitted by law, {COMPANY_NAME} will not be liable for indirect,
        incidental, special, consequential, exemplary, or punitive damages, or for lost profits,
        lost revenue, lost data, business interruption, or procurement of substitute services.
        Our aggregate liability for claims relating to the service is limited to the greater of
        100 CAD or the fees you paid to us for the service in the three months before the claim.
      </p>
    </section>

    <section>
      <h2>Indemnity</h2>
      <p>
        To the fullest extent permitted by law, you will defend, indemnify, and hold harmless{' '}
        {COMPANY_NAME}, its affiliates, and their respective personnel, licensors, and
        service providers against third-party claims and proceedings arising from your
        Customer Content, your breach of these Terms, or your violation of applicable law
        or third-party rights, including resulting judgments, damages, approved settlements,
        and reasonable legal fees and defense costs.
      </p>
      <p>
        We will give reasonably prompt notice of a claim, subject to legal restrictions,
        and reasonable cooperation at your expense. We may participate in the defense
        through our own counsel. You may not settle a claim in a manner that admits fault
        by us, imposes obligations on us, or fails to release the protected parties without
        our prior written consent.
      </p>
      <p>
        These obligations survive termination for matters arising during your use.
        Limitations on {COMPANY_NAME}’ liability do not limit your indemnification
        obligations. Nothing in these provisions excludes liability or obligations that
        applicable law does not permit to be excluded.
      </p>
    </section>

    <section>
      <h2>Suspension and Termination</h2>
      <p>
        We may suspend or terminate access if needed to address security, legal, payment,
        abuse, or material service-integrity risks. You may stop using the service at any time.
      </p>
      <p>
        Where we reasonably suspect a violation of these Terms, unlawful activity,
        infringement, or a risk to rights, safety, or security, we may reject an upload,
        stop processing, quarantine content, restrict access, remove content, or suspend
        or terminate access. We may terminate accounts for repeated or serious infringement
        or other violations.
      </p>
      <p>
        To the extent permitted or required by law, we may preserve relevant content and
        associated account information, metadata, access records, and logs to investigate
        suspected violations, preserve evidence, comply with legal obligations, or establish,
        exercise, or defend legal claims. Restricted evidence may be retained despite removal
        from user access, an account closure, or a deletion request, but only for the period
        lawfully necessary or required.
      </p>
      <p>
        We may report suspected unlawful activity and disclose relevant information to
        competent law-enforcement authorities, regulators, courts, or designated reporting
        bodies where permitted or required by law. We may act without advance notice where
        legally permitted, and may withhold notice where prohibited by law or where notice
        could prejudice an investigation.
      </p>
    </section>

    <section>
      <h2>Governing Law</h2>
      <p>
        These Terms are governed by the laws of Ontario and the federal laws of Canada that
        apply there, without regard to conflict-of-law rules. Courts located in Ontario have
        jurisdiction, unless applicable law requires otherwise.
      </p>
    </section>

    <section>
      <h2>Contact</h2>
      <p>
        Questions about these Terms can be sent to{' '}
        <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
      </p>
      <p>
        Copyright and unlawful-content complaints may be sent to{' '}
        <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>. Identify the material and
        its location within our service, the rights or legal concern involved, and your
        contact information. Provide identifying details rather than attaching unlawful
        material. We may request supporting information and will handle notices, forwarding,
        preservation, and other required actions in accordance with applicable law.
      </p>
    </section>
  </>
);

const PrivacyContent: React.FC = () => (
  <>
    <section>
      <h2>Overview</h2>
      <p>
        This Privacy Policy explains how {COMPANY_NAME} collects, uses, shares, and protects
        personal information through {PRODUCT_NAME}, including the public sign-in
        page and the SaaS application.
      </p>
    </section>

    <section>
      <h2>Information We Collect</h2>
      <p>
        We collect account information such as email address, authentication identifiers,
        display name, company, job title, phone number, timezone, and profile settings when
        you provide or authorize it.
      </p>
      <p>
        We process service content such as uploaded videos, prompts, metadata, search queries,
        indexes, API key settings, webhook settings, connector settings, import jobs, usage
        records, feedback, support messages, and related diagnostic information.
      </p>
      <p>
        We also receive technical information such as IP address, browser and device data,
        authentication events, security logs, local storage preferences, and approximate
        usage activity. Billing information may be processed by a payment provider and only
        limited billing status or transaction metadata is available to us.
      </p>
      <p>
        If you consent to affiliate referral measurement, we process a referral token, click and
        conversion events, paid amounts, and related commission amounts. The browser sends Tolt
        an opaque, program-specific lead identifier rather than your name, email address, or
        VectorMethods account identifier. Eligible payments are synchronized with Tolt through
        Tolt's native Stripe integration.
      </p>
    </section>

    <section>
      <h2>How We Use Information</h2>
      <p>
        We use information to create and secure accounts, authenticate users, provide video
        analysis and media intelligence features, operate APIs and webhooks, manage billing and usage,
        prevent abuse, troubleshoot, support customers, improve reliability, send service
        notices, operate our affiliate program, honor user preferences, and comply with law.
      </p>
      <p>
        We send marketing communications only where permitted by law and user preferences.
        You can opt out of marketing communications without affecting transactional or
        security-related messages.
      </p>
    </section>

    <section>
      <h2>Google Drive Uploads</h2>
      <p>
        Google Drive uploads are optional. When you choose downloadable video, audio, or image
        files in Google Picker and select Upload Selected, VideoVector reads their content and
        file metadata, such as name, type, size, and modification information, to validate and
        copy them into your selected VideoVector index. We request per-file access through
        Google's <code>drive.file</code> permission. This feature does not import folders,
        synchronize Drive, or export files to Drive.
      </p>
      <p>
        The copies and related metadata become customer content. We use our storage, hosting,
        and media and AI processing service providers to provide the features you request,
        including indexing, extraction, and search, as we do for direct uploads. These providers
        receive the content and related data needed to provide those services, subject to the
        sharing and security practices in this policy.
      </p>
      <p>
        Google supplies a short-lived access token for selection and upload. We handle that
        token temporarily in browser memory and the upload request; we do not persist it,
        obtain or store a refresh token, or create a connected-account record for this feature.
        The access token expires naturally. Your authorization may remain in your Google
        Account after an upload, and you can remove it in{' '}
        <a href="https://myaccount.google.com/connections">Google Account connections</a>.
      </p>
      <p>
        To delete a copied file, open its index in VideoVector and use the media item's delete
        action. Copies follow the same retention and deletion rules as direct uploads,
        described below. Deleting a source file in Drive or removing Google authorization does
        not delete copies already stored in VideoVector; deleting a VideoVector copy does not
        delete the source file in Drive. Contact{' '}
        <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> for help with deletion requests.
      </p>
      <p>
        Our use of information received from Google APIs complies with the{' '}
        <a href="https://developers.google.com/terms/api-services-user-data-policy">
          Google API Services User Data Policy
        </a>, including applicable Limited Use requirements.
      </p>
    </section>

    <section>
      <h2>Legal Bases</h2>
      <p>
        Where GDPR, UK GDPR, or similar law applies, we rely on contract necessity to provide
        the service, legitimate interests to secure and improve it, consent for optional
        marketing and non-essential cookies or similar technologies, and legal obligation
        where compliance requires processing.
      </p>
    </section>

    <section>
      <h2>Sharing</h2>
      <p>
        We share information with service providers that help provide hosting, authentication,
        storage, infrastructure, payment, email, support, security, observability, and similar
        business operations. Providers may process data only as needed to provide their services
        to us or as otherwise permitted by law.
      </p>
      <p>
        When you accept affiliate referral measurement, Tolt processes the opaque referral and
        Stripe transaction data needed to attribute a signup or purchase and calculate affiliate
        commissions. An assigned affiliate may see a redacted customer email and paid amount in
        their own program records. Affiliates do not receive your name, full email address,
        VectorMethods account identifier, VectorMethods product user IDs, or system-wide statistics.
      </p>
      <p>
        We may disclose information to comply with law, protect rights and safety, enforce
        terms, investigate abuse, complete a business transaction, or with your direction.
        We do not sell personal information or share it for cross-context behavioral advertising
        as those terms are commonly used under California privacy law.
      </p>
    </section>

    <section>
      <h2>International Processing</h2>
      <p>
        Information may be processed in Canada, the United States, and other locations where
        we or our service providers operate. When required, we use appropriate safeguards for
        cross-border transfers.
      </p>
    </section>

    <section>
      <h2>Retention</h2>
      <p>
        We retain personal information for as long as needed to provide the service, maintain
        accounts, meet legal and accounting obligations, resolve disputes, enforce agreements,
        maintain security, and preserve backups. Customer content is retained until deleted,
        the account is closed, or retention is otherwise required or permitted by law.
      </p>
    </section>

    <section>
      <h2>Your Rights</h2>
      <p>
        Depending on where you live, you may have rights to access, correct, delete, export,
        restrict, or object to certain processing; withdraw consent; opt out of certain uses;
        and complain to a privacy regulator. California residents may also request to know
        categories and specific pieces of personal information, correct or delete personal
        information, opt out of sale or sharing, limit certain sensitive personal information
        uses, and be free from discrimination for exercising privacy rights.
      </p>
      <p>
        Canadian users may request access to personal information, challenge its accuracy,
        ask questions about our privacy practices, and withdraw consent subject to legal or
        contractual limits. Send requests to <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
        We may need to verify your request before acting on it.
      </p>
    </section>

    <section>
      <h2>Children and Sensitive Data</h2>
      <p>
        The service is not intended for children. Do not submit sensitive, regulated, biometric,
        health, financial, or children's data unless you have the authority, consents, notices,
        and safeguards required by law and by your agreement with us.
      </p>
    </section>

    <section>
      <h2>Security</h2>
      <p>
        We use administrative, technical, and organizational safeguards designed to protect
        information. No online service can guarantee absolute security, so customers should
        use strong passwords, protect API keys, limit access, and review integrations.
      </p>
    </section>

    <section>
      <h2>Changes and Contact</h2>
      <p>
        We may update this Policy as the service or law changes. Material updates will be
        posted here or communicated through the service where appropriate. Contact us at{' '}
        <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
      </p>
    </section>
  </>
);

const CookiesContent: React.FC = () => (
  <>
    <section>
      <h2>What We Use</h2>
      <p>
        The public site and application use cookies, local storage, session storage, IndexedDB,
        and similar browser storage where needed for sign-in, security, user preferences,
        performance, cached media, and product functionality.
      </p>
    </section>

    <section>
      <h2>Necessary Storage</h2>
      <p>
        Necessary storage supports authentication, account sessions, fraud and abuse prevention,
        theme preferences, active-tab preferences, interface sizing, cached search or video
        state, and your cookie/storage banner choice. Disabling this storage may prevent the
        service from working correctly.
      </p>
    </section>

    <section>
      <h2>Optional Analytics and Affiliate Attribution</h2>
      <p>
        Optional analytics help us understand service performance. If you accept affiliate
        referral measurement, Tolt sets first-party <code>tolt_referral</code> and
        <code>tolt_data</code> cookies on the VectorMethods domain to connect an affiliate link
        click with a later verified signup and eligible purchase. These cookies last for up to 90
        days. We do not use them for cross-site behavioral advertising.
      </p>
      <p>
        The browser sends Tolt an opaque lead identifier rather than your name, email address, or
        VectorMethods account identifier. Eligible payments are synchronized through Tolt's native
        Stripe integration. An assigned affiliate may see a redacted customer email and paid amount
        for its own referrals, but not your name, full email address, VectorMethods account
        identifier, VectorMethods product user IDs, or system-wide statistics.
      </p>
    </section>

    <section>
      <h2>Your Choices</h2>
      <p>
        You can accept optional analytics and affiliate measurement, or keep only necessary
        storage. Withdrawing optional consent deletes the Tolt referral cookies on this device
        and prevents new affiliate tracking until you consent again. Browser controls can also
        block or delete cookies and site data. If a recognized privacy signal applies to sale or
        sharing opt-outs, we will treat it consistently with applicable law.
      </p>
      <button type="button" className="legal-storage-settings" onClick={openStorageSettings}>
        Review cookie settings
      </button>
    </section>

    <section>
      <h2>Contact</h2>
      <p>
        Questions about cookies or browser storage can be sent to{' '}
        <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
      </p>
    </section>
  </>
);

const AccessibilityContent: React.FC = () => (
  <>
    <section>
      <h2>Commitment</h2>
      <p>
        {COMPANY_NAME} aims to make the public website and key application flows usable by
        people with disabilities. Our practical target is WCAG 2.2 Level AA, while recognizing
        accessibility expectations under laws and norms such as the ADA and AODA.
      </p>
    </section>

    <section>
      <h2>Measures</h2>
      <p>
        We use semantic structure, labeled form controls, keyboard-accessible controls, visible
        focus states, responsive layouts, sufficient contrast, and screen-reader-friendly
        error messaging where feasible. We review new public-facing pages against these
        expectations as part of normal product work.
      </p>
    </section>

    <section>
      <h2>Known Limitations</h2>
      <p>
        Some customer-uploaded video, audio, image, or metadata content may not include captions,
        transcripts, alt text, or other accessible alternatives unless the customer provides or
        generates them. Some third-party authentication, billing, infrastructure, or browser
        interfaces may be outside our direct control.
      </p>
    </section>

    <section>
      <h2>Feedback</h2>
      <p>
        If you find an accessibility barrier, contact{' '}
        <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> with the page, browser,
        assistive technology, and issue you encountered. We will review reasonable accessibility
        requests and work to address confirmed barriers.
      </p>
    </section>
  </>
);

export default LegalPage;
